import config from "@/config";
import { getAccessToken } from "@/lib/auth";
import axios from "@/lib/axios";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

import { Message } from "../components/ChatMessage";
import { Thread } from "../types/thread";

/**
 * Content block types following LangGraph Studio format
 * Supports multimodal content (text, files, images)
 */
export interface TextContentBlock {
  type: "text";
  text: string;
}

export interface FileContentBlock {
  type: "file";
  /** Base64-encoded file data */
  data: string;
  /** MIME type of the file */
  mimeType: string;
  /** Optional filename */
  filename?: string;
}

export interface ImageContentBlock {
  type: "image";
  /** Base64-encoded image data */
  data: string;
  /** MIME type (e.g., "image/png", "image/jpeg") */
  mimeType: string;
}

export type ContentBlock =
  | TextContentBlock
  | FileContentBlock
  | ImageContentBlock;

export interface StreamMessageParams {
  threadId: string;
  message: string;
  assistantId?: string;
  /** Optional content blocks (files, images) following LangGraph Studio format */
  content?: ContentBlock[];
}

export interface MessageChunk {
  type: "token" | "done" | "error";
  content?: string;
  thinking?: string;
  error?: string;
}

/**
 * Get messages for a thread from LangGraph
 * Fetches the full thread state and extracts only human and AI messages
 */
export const getThreadMessages = async (
  threadId: string,
): Promise<Message[]> => {
  const response = await axios.get<Thread>(`/chat/threads/${threadId}`);

  const messages = response.data.values?.messages || [];

  // Filter and transform messages - only show human and ai, hide tool calls
  return messages
    .filter((msg) => msg.type === "human" || msg.type === "ai")
    .map((msg) => {
      // Handle different content formats
      let content = "";
      let thinking = "";
      let attachmentCount = 0;

      if (typeof msg.content === "string") {
        // OpenAI format: content is a string
        content = msg.content;
      } else if (Array.isArray(msg.content)) {
        // Claude format: content is an array of objects with text/thinking/files
        for (const item of msg.content as any[]) {
          if (item.type === "text" && item.text) {
            content += (content ? "\n" : "") + item.text;
          } else if (item.type === "thinking" && item.thinking) {
            thinking += (thinking ? "\n" : "") + item.thinking;
          } else if (
            item.type === "image" ||
            item.type === "file" ||
            item.type === "document"
          ) {
            // Count file/image/document attachments
            attachmentCount++;
          }
        }
      }

      return {
        id: msg.id,
        content,
        thinking: thinking || undefined,
        role: msg.type === "human" ? ("user" as const) : ("assistant" as const),
        timestamp: new Date(), // LangGraph doesn't provide timestamps in messages
        attachmentCount: attachmentCount > 0 ? attachmentCount : undefined,
      };
    });
};

/**
 * Send a message and stream the AI response using Server-Sent Events (SSE)
 * Supports text-only messages or multimodal content (files, images)
 */
export async function* streamMessage({
  threadId,
  message,
  assistantId = "stacey",
  content,
}: StreamMessageParams): AsyncGenerator<MessageChunk> {
  let token: string | undefined;
  try {
    token = await getAccessToken();
  } catch {
    // Auth not available — proceed without token (server will 401).
  }

  const url = `${config.apiUrl}/chat/threads/${threadId}/messages/stream`;

  const body: Record<string, unknown> = {
    message,
    assistantId,
    ...(content && content.length > 0 ? { content } : {}),
  };

  const response = await fetchWithAuth(
    url,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
    },
    token,
  );

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error("No response body");
  }

  try {
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        yield { type: "done" };
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (!trimmedLine || trimmedLine.startsWith(":")) {
          continue; // Skip empty lines and comments
        }

        // SSE format: "data: {...}"
        if (!trimmedLine.startsWith("data: ")) {
          continue;
        }

        try {
          // Remove "data: " prefix and parse JSON
          const jsonStr = trimmedLine.slice(6);
          const event = JSON.parse(jsonStr);

          // Handle streaming tokens from chat model
          if (
            event.event === "events" &&
            event.data?.event === "on_chat_model_stream"
          ) {
            const chunk = event.data?.data?.chunk;

            if (chunk?.content) {
              let content = "";
              let thinking = "";

              // Handle different content formats
              if (typeof chunk.content === "string") {
                // OpenAI format: content is a string
                content = chunk.content;
              } else if (Array.isArray(chunk.content)) {
                // Claude format: content is an array with text/thinking objects
                for (const item of chunk.content as any[]) {
                  if (item.type === "text" && item.text) {
                    content += item.text;
                  } else if (item.type === "thinking" && item.thinking) {
                    thinking += item.thinking;
                  }
                }
              }

              // Yield tokens - either content, thinking, or both
              if (content || thinking) {
                yield {
                  type: "token",
                  content: content || undefined,
                  thinking: thinking || undefined,
                };
              }
            }
          }
          // Handle potential error events
          else if (event.event === "error") {
            yield {
              type: "error",
              error: event.data?.message || "Stream error",
            };
          }
        } catch (e) {
          console.error(
            "Failed to parse SSE line:",
            trimmedLine.substring(0, 100),
            e,
          );
        }
      }
    }

    yield { type: "done" };
  } finally {
    reader.releaseLock();
  }
}
